<div class="min-h-screen w-full min-w-0 overflow-x-hidden bg-cloud-mist">
  <div class="tfr-page-shell py-6">

    <!-- Header -->
    <header class="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h1 class="text-[28px] font-bold text-midnight-indigo">Usuarios</h1>
        <p class="mt-1 text-[13px] text-slate-blue">
          Gestiona cuentas: buscar, activar/desactivar y ajustar comisión de propietarios.
        </p>
      </div>
      <button
        type="button"
        (click)="load()"
        [disabled]="loading()"
        class="inline-flex items-center gap-2 rounded-xl border border-pale-gray bg-white px-3 py-2.5 text-[13px] font-semibold text-midnight-indigo disabled:opacity-50"
      >
        <lucide-icon name="refresh-cw" class="h-[16px] w-[16px]" />
        Recargar
      </button>
    </header>

    <!-- KPIs -->
    <div class="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div class="rounded-2xl border border-pale-gray bg-white p-4">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-steel-gray">Total usuarios</p>
        <p class="mt-1 text-[28px] font-bold text-midnight-indigo">{{ kpis().total }}</p>
      </div>
      <div class="rounded-2xl border border-pale-gray bg-white p-4">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-steel-gray">Propietarios</p>
        <p class="mt-1 text-[28px] font-bold text-midnight-indigo">{{ kpis().owners }}</p>
      </div>
      <div class="rounded-2xl border border-pale-gray bg-white p-4">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-steel-gray">Desactivados</p>
        <p class="mt-1 text-[28px] font-bold text-midnight-indigo">{{ kpis().disabled }}</p>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
      <div class="relative flex-1">
        <lucide-icon name="search" class="pointer-events-none absolute left-3 top-1/2 h-[16px] w-[16px] -translate-y-1/2 text-steel-gray" />
        <input
          type="search"
          [ngModel]="search()"
          (ngModelChange)="search.set($event)"
          placeholder="Buscar por email, nombre o UID…"
          class="w-full rounded-xl border border-pale-gray bg-white py-2.5 pl-9 pr-3 text-[13px] text-midnight-indigo"
        />
      </div>
      <select
        [ngModel]="roleFilter()"
        (ngModelChange)="roleFilter.set($event)"
        class="rounded-xl border border-pale-gray bg-white px-3 py-2.5 text-[13px] text-midnight-indigo"
      >
        <option value="all">Todos los roles</option>
        <option value="user">Inquilinos</option>
        <option value="owner">Propietarios</option>
        <option value="admin">Admins</option>
      </select>
    </div>

    @if (actionError()) {
      <p class="mb-3 rounded-xl bg-red-50 px-3 py-2 text-[12px] text-red-900">{{ actionError() }}</p>
    }

    <!-- States -->
    @if (loading()) {
      <p class="rounded-2xl border border-pale-gray bg-white p-6 text-center text-[13px] text-slate-blue">
        Cargando usuarios…
      </p>
    } @else if (loadError()) {
      <p class="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-[13px] text-red-900">
        {{ loadError() }}
      </p>
    } @else if (filteredUsers().length === 0) {
      <p class="rounded-2xl border border-pale-gray bg-white p-6 text-center text-[13px] text-slate-blue">
        No hay usuarios que coincidan con el filtro.
      </p>
    } @else {
      <div class="overflow-x-auto rounded-2xl border border-pale-gray bg-white">
        <table class="w-full min-w-[760px] text-left text-[13px]">
          <thead>
            <tr class="border-b border-pale-gray text-[11px] uppercase tracking-wide text-steel-gray">
              <th class="px-4 py-3 font-semibold">Usuario</th>
              <th class="px-4 py-3 font-semibold">Rol</th>
              <th class="px-4 py-3 font-semibold">Alta</th>
              <th class="px-4 py-3 font-semibold">Verificado</th>
              <th class="px-4 py-3 font-semibold">Estado</th>
              <th class="px-4 py-3 font-semibold">Comisión</th>
              <th class="px-4 py-3 text-right font-semibold">Acciones</th>
            </tr>
          </thead>
          <tbody>
            @for (u of filteredUsers(); track u.uid) {
              <tr class="border-b border-outline-gray/60 last:border-0" [class.opacity-60]="u.disabled">
                <td class="px-4 py-3">
                  <p class="font-semibold text-midnight-indigo">{{ u.displayName || '—' }}</p>
                  <p class="text-[12px] text-slate-blue">{{ u.email || '—' }}</p>
                </td>
                <td class="px-4 py-3">
                  <span class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold" [class]="roleBadgeClass(u.role)">
                    {{ roleLabel(u.role) }}
                  </span>
                </td>
                <td class="px-4 py-3 text-slate-blue">
                  {{ u.createdAt ? (u.createdAt | date:'dd/MM/yy') : '—' }}
                </td>
                <td class="px-4 py-3">
                  @if (u.emailVerified) {
                    <lucide-icon name="circle-check" class="h-[18px] w-[18px] text-emerald-600" />
                  } @else {
                    <lucide-icon name="circle-dashed" class="h-[18px] w-[18px] text-steel-gray" />
                  }
                </td>
                <td class="px-4 py-3">
                  @if (u.disabled) {
                    <span class="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-800">Desactivado</span>
                  } @else {
                    <span class="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">Activo</span>
                  }
                </td>
                <td class="px-4 py-3">
                  @if (isOwnerRow(u)) {
                    @if (editingUid() === u.uid) {
                      <div class="flex items-center gap-1">
                        <input
                          type="number" min="0" max="30" step="0.5"
                          [ngModel]="commissionDraftPct()"
                          (ngModelChange)="commissionDraftPct.set($event)"
                          class="w-16 rounded-lg border border-platinum-tint px-2 py-1 text-[13px]"
                        />
                        <span class="text-[12px] text-slate-blue">%</span>
                        <button type="button" (click)="saveCommission(u)" [disabled]="savingUid() === u.uid"
                          class="rounded-lg bg-action-blue px-2 py-1 text-[12px] font-semibold text-white disabled:opacity-50">OK</button>
                        <button type="button" (click)="cancelEditCommission()"
                          class="rounded-lg border border-pale-gray px-2 py-1 text-[12px] text-slate-blue">✕</button>
                      </div>
                    } @else {
                      <button type="button" (click)="startEditCommission(u)"
                        class="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[13px] text-midnight-indigo hover:bg-cloud-mist">
                        {{ commissionLabel(u) }}
                        <lucide-icon name="pencil" class="h-[13px] w-[13px] text-steel-gray" />
                      </button>
                    }
                  } @else {
                    <span class="text-steel-gray">—</span>
                  }
                </td>
                <td class="px-4 py-3 text-right">
                  <button
                    type="button"
                    (click)="toggleDisabled(u)"
                    [disabled]="savingUid() === u.uid"
                    class="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12px] font-semibold disabled:opacity-50"
                    [class.border-red-200]="!u.disabled"
                    [class.text-red-700]="!u.disabled"
                    [class.border-emerald-200]="u.disabled"
                    [class.text-emerald-700]="u.disabled"
                  >
                    @if (u.disabled) {
                      <lucide-icon name="user-check" class="h-[14px] w-[14px]" /> Reactivar
                    } @else {
                      <lucide-icon name="user-x" class="h-[14px] w-[14px]" /> Desactivar
                    }
                  </button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }

  </div>
</div>
